import { Typography } from "@mui/material";
import _ from "lodash";
import { ReactElement } from "react";

import { parseTextAndUrlSegments, replaceURLs } from "@stw/extract-urls";
import LiquidTemplate from "@stw/liquid-template";
import { getEncodingStats } from "@stw/sms-text-encoding";

import { MediaPreview } from "~frontend/components/forms/FormikMediaField";
import { CampaignPersonalizedGifPreview } from "~frontend/components/personalizedGifs/CampaignPersonalizedGifPreview";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";

import { generateSampleContact } from "../campaignEditor/Script/ScriptEditor";
import { CampaignForView } from "./types";

export const ScriptPreview = ({
  campaignId,
  variant,
  sampleContactData,
}: {
  campaignId: string;
  variant: CampaignForView["scriptVariants"][0];
  sampleContactData?: Record<string, string> | null;
}): ReactElement => {
  const tenant = useTenant()!;
  const subaccount = useSubaccountAdminConfig()!;

  const renderResult = LiquidTemplate.fromString(variant.script);
  if (!renderResult.ok) {
    throw new Error("Invalid script template");
  }

  const sampleContact = generateSampleContact({
    parsedTemplate: renderResult.template,
    sampleContactData: sampleContactData ?? {},
  });

  const previewText = renderResult.template.render(sampleContact, {
    autoAddRefcodesToABLinks: subaccount.autoAddRefcodesToABLinks,
    refcode2w: "XXXXXX",
  });

  if (!previewText.ok) {
    return (
      <Typography variant="body1" sx={{ fontStyle: "italic" }}>
        Error rendering script: {previewText.error}
      </Typography>
    );
  }

  // Smart-encode
  const { smartEncoded } = getEncodingStats(
    {
      text: previewText.result,
      hasAttachments: variant.mediaUpload != null,
    },
    tenant
  );

  // Extract URLs and apply link shortener settings
  let displayText: React.ReactNode = smartEncoded;
  const linkShortenerDomainName = variant.overrideLinkShortenerSettings
    ? variant.linkShortenerDomainName
    : subaccount.defaultLinkShortenerDomainName || undefined;

  const textAndUrlSegments = parseTextAndUrlSegments(smartEncoded);

  displayText = replaceURLs(textAndUrlSegments, url => {
    const displayUrl = linkShortenerDomainName
      ? `https://${linkShortenerDomainName}/${_.repeat("x", 6)}`
      : url;

    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        {displayUrl}
      </a>
    );
  });

  const media =
    sampleContactData && variant.mediaContactFieldName
      ? { url: sampleContactData[variant.mediaContactFieldName] }
      : variant.mediaUpload
        ? { upload: variant.mediaUpload }
        : null;

  return (
    <>
      {media && (
        <div style={{ lineHeight: 0, paddingBottom: "8px" }}>
          <MediaPreview media={media} />
        </div>
      )}

      {variant.personalizedGif && (
        <div style={{ lineHeight: 0, paddingBottom: "8px" }}>
          <CampaignPersonalizedGifPreview
            campaignId={campaignId}
            campaignPersonalizedGifId={variant.personalizedGif.id}
            sampleContactData={sampleContactData || {}}
            sx={{ height: "100px", width: "100px" }}
          />
        </div>
      )}

      <Typography
        variant="body1"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {displayText}
      </Typography>
    </>
  );
};
